import { AnyElementsViewerDataType, ElementType } from 'components/element-viewer';
import { ChartMetricsInfoType, MetricViewerDataType } from 'components/metric/MetricViewer';
import { Box } from '@mui/material';
import styles from './ElementPreviewSummary.styles';
import IconMi from 'components/common/icon/IconMi';
import React, { Fragment } from 'react';
import { useElementMetadataSettings } from 'hooks/useElementMetadata';
import { IElementData, IElementDataSettings } from 'components/common/element-data/types';
import ElementDataInfo from 'components/common/element-data/ElementDataInfo';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import isOwnerVisible from 'tools/owners';

export interface IElementPreviewSummary {
    elementInfo: ElementType;
    viewerRequestExtraData: AnyElementsViewerDataType;
    onToggleVisibility: () => void;
    isVisible: boolean;
    isPreview?: boolean;
    isReportMetric?: boolean;
}

export function ElementPreviewSummary({
    elementInfo,
    isPreview,
    isVisible,
    onToggleVisibility,
    viewerRequestExtraData,
    isReportMetric,
}: IElementPreviewSummary) {
    const { configuration }: UserAuth = useAppSelector((state) => state.auth);
    let metadataSettings = useElementMetadataSettings('viewer');

    let metricData: ChartMetricsInfoType | undefined = undefined;
    if (isReportMetric || ['metric', 'multi-metric chart'].includes(elementInfo.row.type)) {
        metricData =
            (viewerRequestExtraData as MetricViewerDataType).metrics.length == 0
                ? undefined
                : (viewerRequestExtraData as MetricViewerDataType).metrics[0];
    }

    const elementData: IElementData = {
        elementId: elementInfo.row.elementId,
        elementType: elementInfo.row.type,
        isReportMetric,
        lastMeasurementValueFormatted: metricData?.currValue,
        additionalMeasureValues: metricData?.additionalMeasureValues,
        lastMeasurementTimeFormatted:
            metricData?.lastMeasurementTimeFormatted ?? elementInfo.row.lastMeasurementTimeFormatted,
        metricTileDisplayPctVariance: metricData?.metricTileDisplayPctVariance,
        metricTileDisplayVarianceMode: metricData?.metricTileDisplayVarianceMode,
        metricTileDisplayVarianceFormatted: metricData?.metricTileDisplayVarianceFormatted,
        metricTileDisplayVarianceTargetValueFormatted: metricData?.metricTileDisplayVarianceTargetValueFormatted,
        pctVarianceText: metricData?.pctVarianceText,
        metricMovingAverageInterval: metricData?.metricMovingAverageInterval,
        movingAverageValue: metricData?.movingAverageValue,
        minValue: metricData?.minValue,
        minReachedOn: metricData?.minReachedOn,
        maxValue: metricData?.maxValue,
        maxReachedOn: metricData?.maxReachedOn,
        descriptionMarkdownInd: elementInfo.row.descriptionMarkdownInd,
        descriptionMarkdown: elementInfo.row.descriptionMarkdown,
        description: elementInfo.row.description,
        certifiedInd: elementInfo.certification.enabled,
        certificationLevelColor: elementInfo.certification.color,
        certificationLevelName: elementInfo.certification.name,
        lastCertifiedByName: elementInfo.certification.lastUpdatedBy,
        lastCertifiedTime: elementInfo.certification.lastUpdatedTime,
        lastCertifiedByEmail: '', //Todo ??
        businessOwner: elementInfo.info.businessOwnerDn,
        businessOwnerEmail: elementInfo.info.businessOwnerEmail,
        technicalOwner: elementInfo.info.technicalOwnerDn,
        technicalOwnerEmail: elementInfo.info.technicalOwnerEmail,
        dataSteward: elementInfo.info.dataStewardDn,
        dataStewardEmail: elementInfo.info.dataStewardEmail,
        projected: elementInfo.row.totalForecastAmountFormatted,
        lastDisplayGenerationTime: elementInfo.row.lastDisplayGenerationTime ?? '',
        lastFileUpdatedTime: '', //Todo ??
        engagement: elementInfo.info.engagement,
        documents: elementInfo.documents,
        customFields: elementInfo.customFields,
        topics: elementInfo.topics,
        linkedReports: elementInfo.related?.linkedReports,
        drillDown: elementInfo.related?.drillDown,
        metricCompareValueUnit: elementInfo.row?.metricCompareValueUnit,
    };

    if (metadataSettings.fields['documents']?.display) {
        metadataSettings.fields['documents'].display = isPreview;
    }

    ['business_owner', 'technical_owner', 'data_steward'].forEach((ownerField) => {
        if (metadataSettings.fields[ownerField]?.display) {
            metadataSettings.fields[ownerField].display =
                isOwnerVisible((configuration.owners as any)[ownerField], 'elements') &&
                isOwnerVisible((configuration.owners as any)[ownerField], 'tile_preview');
        }
    });

    const elementDataSettings: IElementDataSettings = {
        viewSize: 'medium',
        metadata: metadataSettings,
    };

    return (
        <>
            {isVisible && (
                <Box
                    sx={styles.wrapper}
                    className={isPreview ? 'summary--preview' : isReportMetric ? 'summary--report' : 'summary--default'}
                >
                    <Box sx={styles.scrollContainer}>
                        <Box sx={styles.contentHolder}>
                            {!isPreview && (
                                <Box sx={styles.closeIconHandler}>
                                    <IconMi
                                        onClick={onToggleVisibility}
                                        icon={'times'}
                                        fontSize="16"
                                        sx={styles.closeIcon}
                                    />
                                </Box>
                            )}
                            <ElementDataInfo
                                elementData={elementData}
                                elementDataSettings={elementDataSettings}
                                size={elementDataSettings.viewSize}
                            />
                        </Box>
                    </Box>
                </Box>
            )}
        </>
    );
}
